
export default {
  name: "contactsForm",

  props: { value: Array },

  data: () => ({
    dropFiles: [],
    docNames: [], // nomes para exibição local em inclusões
    pending: [], // [{id, name}] – leitura em andamento
    fileBaseUrl: "/uploads/", // ajuste aqui se seus arquivos ficam noutro caminho
  }),

  computed: {
    processing() {
      return this.pending.length > 0;
    },
    documents: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("change", val);
      },
    },
  },

  watch: {
    documents: {
      handler(newArr) {
        const len = Array.isArray(newArr) ? newArr.length : 0;
        if (this.docNames.length > len) this.docNames.splice(len);
        while (this.docNames.length < len) this.docNames.push("Documento");
      },
      immediate: true,
    },
  },

  methods: {
    // --------- Normalizador (aceita string OU objeto com { documento })
    getSrc(it) {
      if (typeof it === "string") return it;
      if (it && typeof it === "object") {
        if (it.documento) return it.documento; // backend
        if (it.url) return it.url; // fallback genérico
      }
      return "";
    },

    // --------- Resolve URL final para abrir no duplo clique
    resolveUrl(src) {
      if (!src) return "";
      if (this.isDataUrl(src) || this.isHttpUrl(src)) return src;
      // nome simples => prefixa com base (ajuste fileBaseUrl conforme seu backend)
      return this.fileBaseUrl + src.replace(/^\/+/, "");
    },

    // --------- Nome a exibir (PDFs/arquivos)
    getDisplayName(index, raw) {
      // 1) Se veio de inclusão e temos o nome do File
      const saved = this.docNames[index];
      if (saved && typeof saved === "string") return saved;

      // 2) Deriva do src
      const src = this.getSrc(raw) || "";
      // nome simples
      if (!this.isDataUrl(src) && !this.isHttpUrl(src)) return src;
      // URL http(s)
      if (this.isHttpUrl(src)) {
        try {
          const u = new URL(src);
          const seg = (u.pathname || "").split("/").pop();
          return decodeURIComponent(seg || "PDF");
        } catch {
          return "PDF";
        }
      }
      // DataURL
      if (this.isPdfDataUrl(src)) return "PDF";
      if (this.isImageDataUrl(src)) return "Imagem";
      return "Documento";
    },

    // --------- Ações
    deleteFile(index) {
      this.documents.splice(index, 1);
      this.docNames.splice(index, 1);
    },

    openItem(src) {
      if (!src) return;
      const url = this.resolveUrl(src);
      try {
        const win = window.open(url, "_blank");
        if (!win) window.location.href = url; // fallback se pop-up bloqueado
      } catch (e) {
        window.location.href = url;
      }
    },

    // --------- Detectores (File do input)
    isImage(file) {
      return file?.type?.startsWith("image/");
    },
    isPdf(file) {
      return (
        file?.type === "application/pdf" ||
        (file?.type === "application/octet-stream" &&
          /\.pdf$/i.test(file?.name || "")) ||
        /\.pdf$/i.test(file?.name || "")
      );
    },

    // --------- Detectores (strings já no array)
    isDataUrl(val) {
      return typeof val === "string" && val.startsWith("data:");
    },
    isImageDataUrl(val) {
      return this.isDataUrl(val) && val.startsWith("data:image/");
    },
    isPdfDataUrl(val) {
      if (!this.isDataUrl(val)) return false;
      if (val.startsWith("data:application/pdf")) return true;
      if (val.startsWith("data:application/octet-stream")) {
        const comma = val.indexOf(",");
        if (comma !== -1) {
          const head = val.slice(comma + 1, comma + 1 + 8);
          if (head.startsWith("JVBERi0")) return true; // "%PDF-" em base64
        }
      }
      const comma = val.indexOf(",");
      if (comma !== -1) {
        const head = val.slice(comma + 1, comma + 1 + 8);
        if (head.startsWith("JVBERi0")) return true;
      }
      return false;
    },

    isHttpUrl(val) {
      return typeof val === "string" && /^https?:\/\//i.test(val);
    },
    // imagem por extensão (URL)
    isImageHttpUrl(val) {
      if (!this.isHttpUrl(val)) return false;
      try {
        const u = new URL(val);
        const path = (u.pathname || "").toLowerCase();
        return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path);
      } catch {
        return false;
      }
    },
    // imagem por extensão (NOME simples, ex.: "abc.jpg")
    isImageFilename(val) {
      if (typeof val !== "string") return false;
      const path = val.toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path);
    },

    // === REGRA FINAL PARA RENDER ===
    // Só é imagem se for dataURL de imagem, URL com extensão de imagem, OU nome simples com extensão de imagem
    isImageItem(val) {
      return (
        this.isImageDataUrl(val) ||
        this.isImageHttpUrl(val) ||
        this.isImageFilename(val)
      );
    },

    // --------- Pending feedback
    startPending(name) {
      const id =
        Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      this.pending.push({ id, name });
      return id;
    },
    finishPending(id) {
      const i = this.pending.findIndex((p) => p.id === id);
      if (i >= 0) this.pending.splice(i, 1);
    },

    // --------- Resize de imagens (para inclusões)
    resizeDataUrl(dataUrl, maxW = 1000, maxH = 1000, quality = 0.9) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (!img.naturalWidth || !img.naturalHeight) {
            return reject(new Error("Imagem com dimensões 0."));
          }
          let w = img.naturalWidth,
            h = img.naturalHeight;
          const ratio = Math.min(maxW / w, maxH / h, 1);
          const nw = Math.round(w * ratio),
            nh = Math.round(h * ratio);

          const canvas = document.createElement("canvas");
          canvas.width = nw;
          canvas.height = nh;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, nw, nh);

          const m = /^data:(image\/[a-zA-Z0-9.+-]+);base64,/.exec(dataUrl);
          const mime = m ? m[1] : "image/jpeg";
          try {
            resolve(canvas.toDataURL(mime, quality));
          } catch {
            resolve(canvas.toDataURL("image/jpeg", quality));
          }
        };
        img.onerror = () =>
          reject(
            new Error(
              "Falha ao carregar a imagem (base64 inválida ou formato não suportado).",
            ),
          );
        img.src = dataUrl;
      });
    },

    // --------- Inclusão de arquivos
    encodeFiles() {
      for (const file of this.dropFiles) {
        if (!file) continue;
        const pendingId = this.startPending(file.name);
        const done = () => this.finishPending(pendingId);
        const pushName = (name) => this.docNames.push(name || "Documento");
        const reader = new FileReader();

        if (this.isImage(file)) {
          reader.onload = async () => {
            try {
              const resized = await this.resizeDataUrl(
                reader.result,
                1000,
                1000,
              );
              this.documents.push(resized);
              pushName(file.name);
            } catch (e) {
              console.error("resize error", e);
            } finally {
              done();
            }
          };
          reader.onerror = (e) => {
            console.error("file read error (image)", e);
            done();
          };
          reader.readAsDataURL(file);
        } else if (this.isPdf(file)) {
          reader.onload = () => {
            const dataUrl = reader.result; // guardamos o PDF como dataURL
            this.documents.push(dataUrl);
            pushName(file.name);
            done();
          };
          reader.onerror = (e) => {
            console.error("file read error (pdf)", e);
            done();
          };
          reader.readAsDataURL(file);
        } else {
          console.warn(
            "Ignorando arquivo não suportado:",
            file.name || "(sem nome)",
            file.type || "(sem type)",
          );
          done();
        }
      }
      this.dropFiles = [];
    },
  },
};
